import { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import api from '../lib/api';
import { toast } from 'sonner';
import { cn } from '../lib/utils';
import {
  User,
  Lock,
  Building2,
  AppWindow,
  Save,
  Loader2,
  Eye,
  EyeOff,
  Mail,
  Phone,
  MapPin,
  Calculator,
  Users,
  Shield,
  Check,
} from 'lucide-react';

const APP_ICONS = {
  'nexus-compta': Calculator,
  'nexus-sirh': Users,
  compta: Calculator,
  sirh: Users,
};

const Settings = () => {
  const { user, fetchUser, hasNexusRole } = useAuth();

  const [profile, setProfile] = useState({ nom: '', prenom: '', email: '' });
  const [profileSaving, setProfileSaving] = useState(false);

  const [passwords, setPasswords] = useState({
    current_password: '',
    new_password: '',
    confirm_password: '',
  });
  const [showPasswords, setShowPasswords] = useState({ current: false, new: false, confirm: false });
  const [passwordSaving, setPasswordSaving] = useState(false);

  const [structure, setStructure] = useState({
    nom: '', siret: '', adresse: '', code_postal: '', ville: '', email: '', telephone: '',
  });
  const [structureSaving, setStructureSaving] = useState(false);

  const [structureId, setStructureId] = useState(null);
  const isAdmin = hasNexusRole('admin_structure') || hasNexusRole('super_admin');

  useEffect(() => {
    if (user) {
      setProfile({
        nom: user.nom || user.last_name || '',
        prenom: user.prenom || user.first_name || '',
        email: user.email || '',
      });
      // Structure may be embedded in user or need separate fetch
      if (user.structure) {
        setStructureId(user.structure.id || user.structure._id || null);
        setStructure({
          nom: user.structure.nom || '',
          siret: user.structure.siret || '',
          adresse: user.structure.adresse || '',
          code_postal: user.structure.code_postal || '',
          ville: user.structure.ville || '',
          email: user.structure.email || '',
          telephone: user.structure.telephone || '',
        });
      } else if (user.structure_id) {
        // Structure not embedded — fetch it separately
        setStructureId(user.structure_id);
        api.get(`/api/structures/${user.structure_id}`)
          .then((res) => {
            const s = res.data;
            setStructure({
              nom: s.nom || '',
              siret: s.siret || '',
              adresse: s.adresse || '',
              code_postal: s.code_postal || '',
              ville: s.ville || '',
              email: s.email || '',
              telephone: s.telephone || '',
            });
          })
          .catch(() => {
            // Structure fetch failed — ignore, user can still edit profile
          });
      }
    }
  }, [user]);

  const handleProfileSave = async (e) => {
    e.preventDefault();
    setProfileSaving(true);
    try {
      await api.put('/api/auth/profile', { nom: profile.nom, prenom: profile.prenom });
      toast.success('Profil mis a jour');
      fetchUser();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erreur lors de la mise a jour');
    } finally {
      setProfileSaving(false);
    }
  };

  const handlePasswordChange = async (e) => {
    e.preventDefault();
    if (passwords.new_password !== passwords.confirm_password) {
      toast.error('Les mots de passe ne correspondent pas');
      return;
    }
    if (passwords.new_password.length < 8) {
      toast.error('Le mot de passe doit contenir au moins 8 caracteres');
      return;
    }
    setPasswordSaving(true);
    try {
      await api.put('/api/auth/change-password', {
        current_password: passwords.current_password,
        new_password: passwords.new_password,
      });
      toast.success('Mot de passe modifie');
      setPasswords({ current_password: '', new_password: '', confirm_password: '' });
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erreur lors du changement de mot de passe');
    } finally {
      setPasswordSaving(false);
    }
  };

  const handleStructureSave = async (e) => {
    e.preventDefault();
    const sId = structureId || user?.structure?.id;
    if (!sId) return;
    setStructureSaving(true);
    try {
      await api.put(`/api/structures/${sId}`, structure);
      toast.success('Structure mise a jour');
      fetchUser();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erreur lors de la mise a jour');
    } finally {
      setStructureSaving(false);
    }
  };

  const PasswordInput = ({ id, label, value, onChange, showKey }) => (
    <div>
      <label htmlFor={id} className="block text-sm font-medium text-slate-700 mb-1">{label}</label>
      <div className="relative">
        <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
        <input
          id={id}
          type={showPasswords[showKey] ? 'text' : 'password'}
          value={value}
          onChange={onChange}
          required
          className="w-full pl-10 pr-12 py-2 rounded-lg border border-slate-300 text-sm focus:outline-none focus:ring-2 focus:ring-nexus-500 focus:border-nexus-500"
        />
        <button
          type="button"
          onClick={() => setShowPasswords((p) => ({ ...p, [showKey]: !p[showKey] }))}
          className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
        >
          {showPasswords[showKey] ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
        </button>
      </div>
    </div>
  );

  return (
    <div className="space-y-8 max-w-3xl">
      <div>
        <h1 className="text-2xl font-bold text-slate-900">Parametres</h1>
        <p className="text-slate-500 text-sm mt-1">Gerez votre profil et vos preferences</p>
      </div>

      {/* Profile Section */}
      <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">
        <div className="px-6 py-4 border-b border-slate-200 bg-slate-50">
          <h2 className="text-base font-semibold text-slate-900 flex items-center gap-2">
            <User className="w-4 h-4 text-nexus-600" />
            Informations personnelles
          </h2>
        </div>
        <form onSubmit={handleProfileSave} className="p-6 space-y-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Prenom</label>
              <div className="relative">
                <User className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                <input
                  type="text"
                  value={profile.prenom}
                  onChange={(e) => setProfile((p) => ({ ...p, prenom: e.target.value }))}
                  className="w-full pl-10 pr-4 py-2 rounded-lg border border-slate-300 text-sm focus:outline-none focus:ring-2 focus:ring-nexus-500 focus:border-nexus-500"
                />
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Nom</label>
              <div className="relative">
                <User className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                <input
                  type="text"
                  value={profile.nom}
                  onChange={(e) => setProfile((p) => ({ ...p, nom: e.target.value }))}
                  className="w-full pl-10 pr-4 py-2 rounded-lg border border-slate-300 text-sm focus:outline-none focus:ring-2 focus:ring-nexus-500 focus:border-nexus-500"
                />
              </div>
            </div>
          </div>
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">Email</label>
            <div className="relative">
              <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <input
                type="email"
                value={profile.email}
                disabled
                className="w-full pl-10 pr-4 py-2 rounded-lg border border-slate-200 text-sm bg-slate-50 text-slate-500 cursor-not-allowed"
              />
            </div>
            <p className="text-xs text-slate-400 mt-1">L'email ne peut pas etre modifie</p>
          </div>
          <div className="flex justify-end pt-2">
            <button
              type="submit"
              disabled={profileSaving}
              className="flex items-center gap-2 px-4 py-2 rounded-lg bg-nexus-600 text-white text-sm font-medium hover:bg-nexus-700 disabled:opacity-50 transition-colors"
            >
              {profileSaving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
              Enregistrer
            </button>
          </div>
        </form>
      </div>

      {/* Change Password */}
      <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">
        <div className="px-6 py-4 border-b border-slate-200 bg-slate-50">
          <h2 className="text-base font-semibold text-slate-900 flex items-center gap-2">
            <Lock className="w-4 h-4 text-nexus-600" />
            Changer le mot de passe
          </h2>
        </div>
        <form onSubmit={handlePasswordChange} className="p-6 space-y-4">
          <PasswordInput
            id="current_password"
            label="Mot de passe actuel"
            value={passwords.current_password}
            onChange={(e) => setPasswords((p) => ({ ...p, current_password: e.target.value }))}
            showKey="current"
          />
          <PasswordInput
            id="new_password"
            label="Nouveau mot de passe"
            value={passwords.new_password}
            onChange={(e) => setPasswords((p) => ({ ...p, new_password: e.target.value }))}
            showKey="new"
          />
          <PasswordInput
            id="confirm_password"
            label="Confirmer le mot de passe"
            value={passwords.confirm_password}
            onChange={(e) => setPasswords((p) => ({ ...p, confirm_password: e.target.value }))}
            showKey="confirm"
          />
          {passwords.new_password && passwords.confirm_password && passwords.new_password !== passwords.confirm_password && (
            <p className="text-sm text-red-600">Les mots de passe ne correspondent pas</p>
          )}
          <div className="flex justify-end pt-2">
            <button
              type="submit"
              disabled={passwordSaving || !passwords.current_password || !passwords.new_password || !passwords.confirm_password}
              className="flex items-center gap-2 px-4 py-2 rounded-lg bg-nexus-600 text-white text-sm font-medium hover:bg-nexus-700 disabled:opacity-50 transition-colors"
            >
              {passwordSaving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Lock className="w-4 h-4" />}
              Changer le mot de passe
            </button>
          </div>
        </form>
      </div>

      {/* Structure Info */}
      {(user?.structure || structureId) && (
        <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">
          <div className="px-6 py-4 border-b border-slate-200 bg-slate-50">
            <h2 className="text-base font-semibold text-slate-900 flex items-center gap-2">
              <Building2 className="w-4 h-4 text-nexus-600" />
              Structure
              {!isAdmin && <span className="text-xs text-slate-400 font-normal ml-2">(lecture seule)</span>}
            </h2>
          </div>
          <form onSubmit={handleStructureSave} className="p-6 space-y-4">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Nom</label>
                <div className="relative">
                  <Building2 className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                  <input
                    type="text"
                    value={structure.nom}
                    onChange={(e) => setStructure((p) => ({ ...p, nom: e.target.value }))}
                    disabled={!isAdmin}
                    className={cn(
                      'w-full pl-10 pr-4 py-2 rounded-lg border text-sm focus:outline-none focus:ring-2 focus:ring-nexus-500 focus:border-nexus-500',
                      isAdmin ? 'border-slate-300 bg-white' : 'border-slate-200 bg-slate-50 text-slate-500 cursor-not-allowed'
                    )}
                  />
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">SIRET</label>
                <input
                  type="text"
                  value={structure.siret}
                  onChange={(e) => setStructure((p) => ({ ...p, siret: e.target.value }))}
                  disabled={!isAdmin}
                  className={cn(
                    'w-full px-4 py-2 rounded-lg border text-sm font-mono focus:outline-none focus:ring-2 focus:ring-nexus-500 focus:border-nexus-500',
                    isAdmin ? 'border-slate-300 bg-white' : 'border-slate-200 bg-slate-50 text-slate-500 cursor-not-allowed'
                  )}
                />
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Adresse</label>
              <div className="relative">
                <MapPin className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                <input
                  type="text"
                  value={structure.adresse}
                  onChange={(e) => setStructure((p) => ({ ...p, adresse: e.target.value }))}
                  disabled={!isAdmin}
                  className={cn(
                    'w-full pl-10 pr-4 py-2 rounded-lg border text-sm focus:outline-none focus:ring-2 focus:ring-nexus-500 focus:border-nexus-500',
                    isAdmin ? 'border-slate-300 bg-white' : 'border-slate-200 bg-slate-50 text-slate-500 cursor-not-allowed'
                  )}
                />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Code postal</label>
                <input
                  type="text"
                  value={structure.code_postal}
                  onChange={(e) => setStructure((p) => ({ ...p, code_postal: e.target.value }))}
                  disabled={!isAdmin}
                  className={cn(
                    'w-full px-4 py-2 rounded-lg border text-sm focus:outline-none focus:ring-2 focus:ring-nexus-500 focus:border-nexus-500',
                    isAdmin ? 'border-slate-300 bg-white' : 'border-slate-200 bg-slate-50 text-slate-500 cursor-not-allowed'
                  )}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Ville</label>
                <input
                  type="text"
                  value={structure.ville}
                  onChange={(e) => setStructure((p) => ({ ...p, ville: e.target.value }))}
                  disabled={!isAdmin}
                  className={cn(
                    'w-full px-4 py-2 rounded-lg border text-sm focus:outline-none focus:ring-2 focus:ring-nexus-500 focus:border-nexus-500',
                    isAdmin ? 'border-slate-300 bg-white' : 'border-slate-200 bg-slate-50 text-slate-500 cursor-not-allowed'
                  )}
                />
              </div>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Email structure</label>
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                  <input
                    type="email"
                    value={structure.email}
                    onChange={(e) => setStructure((p) => ({ ...p, email: e.target.value }))}
                    disabled={!isAdmin}
                    className={cn(
                      'w-full pl-10 pr-4 py-2 rounded-lg border text-sm focus:outline-none focus:ring-2 focus:ring-nexus-500 focus:border-nexus-500',
                      isAdmin ? 'border-slate-300 bg-white' : 'border-slate-200 bg-slate-50 text-slate-500 cursor-not-allowed'
                    )}
                  />
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Telephone</label>
                <div className="relative">
                  <Phone className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                  <input
                    type="tel"
                    value={structure.telephone}
                    onChange={(e) => setStructure((p) => ({ ...p, telephone: e.target.value }))}
                    disabled={!isAdmin}
                    className={cn(
                      'w-full pl-10 pr-4 py-2 rounded-lg border text-sm focus:outline-none focus:ring-2 focus:ring-nexus-500 focus:border-nexus-500',
                      isAdmin ? 'border-slate-300 bg-white' : 'border-slate-200 bg-slate-50 text-slate-500 cursor-not-allowed'
                    )}
                  />
                </div>
              </div>
            </div>
            {isAdmin && (
              <div className="flex justify-end pt-2">
                <button
                  type="submit"
                  disabled={structureSaving}
                  className="flex items-center gap-2 px-4 py-2 rounded-lg bg-nexus-600 text-white text-sm font-medium hover:bg-nexus-700 disabled:opacity-50 transition-colors"
                >
                  {structureSaving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
                  Enregistrer
                </button>
              </div>
            )}
          </form>
        </div>
      )}

      {/* Connected Apps */}
      <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">
        <div className="px-6 py-4 border-b border-slate-200 bg-slate-50">
          <h2 className="text-base font-semibold text-slate-900 flex items-center gap-2">
            <AppWindow className="w-4 h-4 text-nexus-600" />
            Applications connectees
          </h2>
        </div>
        <div className="p-6">
          {user?.app_roles && Object.keys(user.app_roles).length > 0 ? (
            <div className="space-y-3">
              {Object.entries(user.app_roles).map(([appCode, role]) => {
                const AppIcon = APP_ICONS[appCode] || AppWindow;
                return (
                  <div key={appCode} className="flex items-center justify-between p-4 bg-slate-50 rounded-lg">
                    <div className="flex items-center gap-3">
                      <div className="w-9 h-9 rounded-lg bg-nexus-50 flex items-center justify-center">
                        <AppIcon className="w-4 h-4 text-nexus-600" />
                      </div>
                      <div>
                        <p className="font-medium text-slate-900 capitalize">{appCode}</p>
                        <p className="text-xs text-slate-500">Application Nexus</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <Shield className="w-3.5 h-3.5 text-slate-400" />
                      <span className="text-sm font-medium text-slate-700">{typeof role === 'object' ? role?.role : role}</span>
                      <Check className="w-4 h-4 text-green-500" />
                    </div>
                  </div>
                );
              })}
            </div>
          ) : (
            <div className="text-center py-8">
              <AppWindow className="w-10 h-10 text-slate-300 mx-auto mb-3" />
              <p className="text-sm text-slate-500">Aucune application connectee</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default Settings;
